import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { campaignTaskTrackingQueryOptions } from '@/lib/query-options';
import { invalidateCampaignReverseSync, shouldInvalidateReverseSync } from '@/lib/reverse-sync-invalidation';
import type { CampaignData, TaskTrackingSummary } from '@/lib/types';

/**
 * Durable reverse-sync cache invalidation (#5745).
 *
 * Mounted once in the campaign _slug layout, which does NOT remount on campaign↔ad navigation.
 * It watches the campaign_reverse_sync task-tracking summary — the same poller _slug already mounts,
 * so this subscribes to the shared cache without issuing an extra request — and invalidates every
 * reverse-sync-affected query exactly once per completion. This replaces the per-instance
 * invalidation that used to live in useReverseSync, which double-fired on the campaign index (two
 * mounted instances) and was lost when navigating to an ad sub-page.
 *
 * Fires on EITHER:
 *   - an in_progress 1→0 edge (a foreground sync we watched run to completion), OR
 *   - a completed/total increment while in_progress is 0 (the nightly scheduled job writes a
 *     terminal COMPLETED row directly — Gap 4 — so it never passes through in_progress > 0).
 *
 * The first observed snapshot only seeds the baseline (never invalidates), so opening a page onto
 * an already-cached "N completed" summary does not fire spuriously.
 */
export function useReverseSyncInvalidation(campaign: CampaignData) {
  const campaignId = campaign.id;
  const slug = campaign.slug;
  const queryClient = useQueryClient();

  // Subscribe to the same poller _slug.tsx mounts (identical key → shared cache, no extra fetch).
  const { data } = useQuery(campaignTaskTrackingQueryOptions(campaignId));
  const summary = data?.campaign_reverse_sync;

  const prevRef = useRef<TaskTrackingSummary | null>(null);

  useEffect(() => {
    if (!summary) return;

    const prev = prevRef.current;
    prevRef.current = summary;

    if (shouldInvalidateReverseSync(prev, summary)) {
      invalidateCampaignReverseSync(queryClient, campaignId, slug);
    }
  }, [summary, campaignId, slug, queryClient]);
}
